import React, { useEffect, useMemo, useRef, useState } from "react";
import { EmptyStateErro, EmptyStateSemPlanilha } from "../../components/EmptyState";
import { AnimatePresence } from "framer-motion";
import { FiX } from "react-icons/fi";
import FilterPanel from "../../components/FilterPanel";
import BotaoExportacao from "../../components/Exportacao";
import { PageLoader } from "../../components/LoadingMarcenaria";
import {
  ColumnResizeProvider,
  ResetColumnsButton,
  ResizableColgroup,
  ResizableTh,
} from "../../components/ResizableColumns";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import {
  BuscaInput,
  FilterField,
  FilterGrid,
  FilterLabel,
  FiltroSelect as SharedFiltroSelect,
} from "../../components/PainelTabela/styles";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { isNo } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { isBeforeToday } from "../../utils/status";
import { getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";
import {
  Aba,
  Abas,
  AcumBadge,
  BarraInline,
  CardLabel,
  CardSub,
  CardValor,
  CardWrapper,
  ClasseBadge,
  Grid,
  LegendaABC,
  LegendaItem,
  ModalCard,
  ModalCloseButton,
  ModalHeader,
  ModalMeta,
  ModalOverlay,
  ModalTable,
  ModalTableWrapper,
  ModalTitle,
  ResumoABC,
  ResumoCard,
  ResumoClasse,
  ResumoInfo,
  ResumoReceita,
  Secao,
  SecaoTitulo,
  Tabela,
  TabelaWrapper,
  Td,
  TdBar,
  Th,
  TipoButton,
  Titulo,
  TopBar,
  TopBarAcoes,
  Tr,
  Wrapper,
} from "./styles";

const CLASSE_COR = { A: "var(--success-text)", B: "var(--warning-text)", C: "var(--danger-text)" };
const CLASSE_BG = { A: "var(--success-bg)", B: "var(--warning-bg)", C: "var(--danger-bg)" };
const INITIAL_FILTERS = {
  busca: "",
  vendedor: "TODOS",
  situacao: "TODAS",
  tipo: "TODOS",
};

function getUniqueOptions(values) {
  return [...new Set(values.filter(Boolean).map((value) => String(value).trim()))].sort((left, right) =>
    left.localeCompare(right, "pt-BR", { numeric: true, sensitivity: "base" })
  );
}

function KPICard({ label, valor, sub, delay = 0, destaque = false }) {
  return (
    <CardWrapper
      $tone={destaque ? "danger" : "default"}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25 }}
    >
      <CardLabel>{label}</CardLabel>
      <CardValor $tone={destaque ? "danger" : "default"}>{valor}</CardValor>
      {sub && <CardSub>{sub}</CardSub>}
    </CardWrapper>
  );
}

function getSituacaoAmbiente(row) {
  const dataLimite = row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA];

  if (row[F.CARREGAMENTO]) {
    return "Entregue";
  }

  if (isBeforeToday(dataLimite)) {
    return "Em atraso";
  }

  return "Em aberto";
}

export default function VisaoFinanceira() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [abaAtiva, setAbaAtiva] = useState("resumo");
  const [tipoModalAberto, setTipoModalAberto] = useState(null);
  const pageRef = useRef(null);

  const ativos = useMemo(() => dados.filter((row) => isNo(row[F.OBRA_FUTURA])), [dados]);
  const vendedorOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(ativos.map((row) => row[F.VENDEDOR]))],
    [ativos]
  );
  const tipoOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(ativos.map((row) => getTipoAmbienteSeguro(row)))],
    [ativos]
  );

  const filtrados = useMemo(
    () =>
      ativos.filter((row) => {
        const tipo = getTipoAmbienteSeguro(row);
        const situacao = getSituacaoAmbiente(row);
        const query = filters.busca.trim().toLowerCase();

        if (
          query &&
          ![row[F.NOME_DO_CLIENTE], row[F.AMBIENTE], row[F.VENDEDOR], tipo, situacao]
            .join(" ")
            .toLowerCase()
            .includes(query)
        ) {
          return false;
        }

        if (filters.vendedor !== "TODOS" && row[F.VENDEDOR] !== filters.vendedor) {
          return false;
        }

        if (filters.tipo !== "TODOS" && tipo !== filters.tipo) {
          return false;
        }

        if (filters.situacao !== "TODAS" && situacao !== filters.situacao) {
          return false;
        }

        return true;
      }),
    [ativos, filters]
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.busca.trim()) chips.push(`Busca: ${filters.busca.trim()}`);
    if (filters.vendedor !== "TODOS") chips.push(`Vendedor: ${filters.vendedor}`);
    if (filters.tipo !== "TODOS") chips.push(`Tipo: ${filters.tipo}`);
    if (filters.situacao !== "TODAS") chips.push(`Situação: ${filters.situacao}`);
    return chips;
  }, [filters]);

  const kpis = useMemo(() => {
    if (!dados.length) {
      return null;
    }

    const receitaTotal = filtrados.reduce(
      (accumulator, row) => accumulator + (Number(row[F.VALOR_DO_AMBIENTE]) || 0),
      0
    );
    const receitaEntregues = filtrados
      .filter((row) => Boolean(row[F.CARREGAMENTO]))
      .reduce(
        (accumulator, row) => accumulator + (Number(row[F.VALOR_DO_AMBIENTE]) || 0),
        0
      );
    const receitaWip = filtrados
      .filter((row) => !row[F.CARREGAMENTO])
      .reduce(
        (accumulator, row) => accumulator + (Number(row[F.VALOR_DO_AMBIENTE]) || 0),
        0
      );

    const totalAmbientes = filtrados.length;
    const ticketMedioAmb = totalAmbientes ? receitaTotal / totalAmbientes : 0;

    const clientes = [...new Set(filtrados.map((row) => row[F.NOME_DO_CLIENTE]).filter(Boolean))];
    const ticketMedioCli = clientes.length ? receitaTotal / clientes.length : 0;

    const emAberto = filtrados.filter((row) => !row[F.CARREGAMENTO]);
    const atrasados = emAberto.filter((row) =>
      isBeforeToday(row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA])
    );
    const percAtraso = emAberto.length ? ((atrasados.length / emAberto.length) * 100).toFixed(1) : "0.0";

    const diasAtraso = atrasados
      .map((row) => {
        const dataLimite = row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA];
        return dataLimite
          ? Math.floor((Date.now() - new Date(dataLimite).getTime()) / 86400000)
          : null;
      })
      .filter((valor) => valor !== null);

    const diasMediosAtraso = diasAtraso.length
      ? (
          diasAtraso.reduce((accumulator, valor) => accumulator + valor, 0) /
          diasAtraso.length
        ).toFixed(1)
      : null;

    const porVendedor = vendedorOptions
      .filter((vendedor) => vendedor !== "TODOS")
      .map((vendedor) => {
        const linhas = filtrados.filter((row) => row[F.VENDEDOR] === vendedor);
        const receita = linhas.reduce(
          (accumulator, row) => accumulator + (Number(row[F.VALOR_DO_AMBIENTE]) || 0),
          0
        );
        const qtd = linhas.length;

        return {
          vendedor,
          receita,
          qtd,
          ticket: qtd ? receita / qtd : 0,
        };
      })
      .sort((left, right) => right.receita - left.receita);

    const porTipo = {};

    filtrados.forEach((row) => {
      const tipo = getTipoAmbienteSeguro(row);
      const valor = Number(row[F.VALOR_DO_AMBIENTE]) || 0;
      const entregue = Boolean(row[F.CARREGAMENTO]);
      const prazo = row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA];

      if (!porTipo[tipo]) {
        porTipo[tipo] = {
          receita: 0,
          qtd: 0,
          entregues: 0,
          ambientes: [],
        };
      }

      porTipo[tipo].receita += valor;
      porTipo[tipo].qtd += 1;
      porTipo[tipo].entregues += entregue ? 1 : 0;
      porTipo[tipo].ambientes.push({
        cliente: row[F.NOME_DO_CLIENTE] || "—",
        ambiente: row[F.AMBIENTE] || "—",
        vendedor: row[F.VENDEDOR] || "—",
        prazo,
        situacao: getSituacaoAmbiente(row),
        valor,
      });
    });

    const tiposOrdenados = Object.entries(porTipo)
      .map(([tipo, valores]) => ({
        tipo,
        ...valores,
        ticket: valores.qtd ? valores.receita / valores.qtd : 0,
        ambientes: [...valores.ambientes].sort((left, right) => right.valor - left.valor),
      }))
      .sort((left, right) => right.receita - left.receita);

    const receitaTotalTipos = tiposOrdenados.reduce(
      (accumulator, tipo) => accumulator + tipo.receita,
      0
    );
    let acumulado = 0;

    const curvaAbc = tiposOrdenados.map((tipo) => {
      const participacao = receitaTotalTipos ? (tipo.receita / receitaTotalTipos) * 100 : 0;
      acumulado += participacao;
      const classe = acumulado <= 80 ? "A" : acumulado <= 95 ? "B" : "C";

      return {
        ...tipo,
        participacao: participacao.toFixed(1),
        acumulado: acumulado.toFixed(1),
        classe,
      };
    });

    return {
      receitaTotal,
      receitaEntregues,
      receitaWip,
      totalAmbientes,
      ticketMedioAmb,
      ticketMedioCli,
      percAtraso,
      diasMediosAtraso,
      atrasados: atrasados.length,
      porVendedor,
      curvaAbc,
    };
  }, [dados, filtrados, vendedorOptions]);

  const tipoSelecionado = useMemo(
    () => kpis?.curvaAbc.find((tipo) => tipo.tipo === tipoModalAberto) ?? null,
    [kpis, tipoModalAberto]
  );

  useEffect(() => {
    if (!tipoSelecionado) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setTipoModalAberto(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [tipoSelecionado]);

  const exportSnapshotPayload = useMemo(() => {
    if (!kpis) return null;
    return {
      titulo: "Visão Financeira",
      resumo: [
        { label: "Receita total",       value: kpis.receitaTotal,     type: "currency" },
        { label: "Receita entregue",    value: kpis.receitaEntregues, type: "currency" },
        { label: "Receita WIP",         value: kpis.receitaWip,       type: "currency" },
        { label: "Ticket médio",        value: kpis.ticketMedio,      type: "currency" },
        { label: "Total de ambientes",  value: kpis.totalAmbientes,   type: "number" },
        { label: "Em atraso",           value: kpis.atrasados,        type: "number" },
        { label: "% em atraso",         value: kpis.percAtraso,       type: "percentage" },
        { label: "Dias médios em produção", value: kpis.diasMedios,   type: "days" },
      ],
      secoes: [
        {
          id: "vendedores",
          label: "Vendedores",
          columns: [
            { key: "vendedor",  label: "Vendedor",  type: "text" },
            { key: "qtd",       label: "Ambientes", type: "number" },
            { key: "receita",   label: "Receita",   type: "currency" },
            { key: "ticket",    label: "Ticket médio", type: "currency" },
          ],
          rows: kpis.porVendedor,
        },
        {
          id: "curvaAbc",
          label: "Curva ABC por Tipo",
          columns: [
            { key: "classe",       label: "Classe",       type: "text" },
            { key: "tipo",         label: "Tipo",         type: "text" },
            { key: "qtd",          label: "Qtd",          type: "number" },
            { key: "entregues",    label: "Entregues",    type: "number" },
            { key: "receita",      label: "Receita",      type: "currency" },
            { key: "ticket",       label: "Ticket",       type: "currency" },
            { key: "participacao", label: "Participação", type: "percentage" },
            { key: "acumulado",    label: "Acumulado",    type: "percentage" },
          ],
          rows: kpis.curvaAbc,
        },
      ],
      visual: {
        ref: pageRef,
        sections: [
          { id: "kpis-receita",     label: "Cards de Receita",       selector: "[data-pdf='kpis-receita']" },
          { id: "kpis-producao",    label: "Cards de Produção/Prazos", selector: "[data-pdf='kpis-producao']" },
          { id: "tabela-vendedores", label: "Tabela por Vendedor",   selector: "[data-pdf='tabela-vendedores']" },
          { id: "curva-abc",        label: "Curva ABC",              selector: "[data-pdf='curva-abc']" },
        ],
      },
    };
  }, [kpis]);
  useExportSnapshot("visao-financeira", exportSnapshotPayload, pageRef);

  if (initialLoading) {
    return <PageLoader scope="financeiro" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  if (!kpis) {
    return <EmptyStateSemPlanilha />;
  }

  const mask = (valor) => (precosVisiveis ? valor : "••••••");

  const exportResumo = [
    { label: "Receita total",       value: kpis.receitaTotal,     type: "currency" },
    { label: "Receita entregue",    value: kpis.receitaEntregues, type: "currency" },
    { label: "Receita WIP",         value: kpis.receitaWip,       type: "currency" },
    { label: "Ticket médio",        value: kpis.ticketMedio,      type: "currency" },
    { label: "Total de ambientes",  value: kpis.totalAmbientes,   type: "number" },
    { label: "Em atraso",           value: kpis.atrasados,        type: "number" },
    { label: "% em atraso",         value: kpis.percAtraso,       type: "percentage" },
    { label: "Dias médios em produção", value: kpis.diasMedios,   type: "days" },
  ];

  const exportSecoes = [
    {
      id: "vendedores",
      label: "Vendedores",
      columns: [
        { key: "vendedor",  label: "Vendedor",  type: "text" },
        { key: "qtd",       label: "Ambientes", type: "number" },
        { key: "receita",   label: "Receita",   type: "currency" },
        { key: "ticket",    label: "Ticket médio", type: "currency" },
      ],
      rows: kpis.porVendedor,
    },
    {
      id: "curvaAbc",
      label: "Curva ABC por Tipo",
      columns: [
        { key: "classe",       label: "Classe",       type: "text" },
        { key: "tipo",         label: "Tipo",         type: "text" },
        { key: "qtd",          label: "Qtd",          type: "number" },
        { key: "entregues",    label: "Entregues",    type: "number" },
        { key: "receita",      label: "Receita",      type: "currency" },
        { key: "ticket",       label: "Ticket",       type: "currency" },
        { key: "participacao", label: "Participação", type: "percentage" },
        { key: "acumulado",    label: "Acumulado",    type: "percentage" },
      ],
      rows: kpis.curvaAbc,
    },
  ];

  const exportVisual = {
    ref: pageRef,
    sections: [
      { id: "kpis-receita",     label: "Cards de Receita",       selector: "[data-pdf='kpis-receita']" },
      { id: "kpis-producao",    label: "Cards de Produção/Prazos", selector: "[data-pdf='kpis-producao']" },
      { id: "tabela-vendedores", label: "Tabela por Vendedor",   selector: "[data-pdf='tabela-vendedores']" },
      { id: "curva-abc",        label: "Curva ABC",              selector: "[data-pdf='curva-abc']" },
    ],
  };

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <Titulo>Visão Financeira</Titulo>
        <TopBarAcoes>
          <BuscaInput
            type="search"
            aria-label="Busca geral na visão financeira"
            value={filters.busca}
            onChange={(event) => setFilters((current) => ({ ...current, busca: event.target.value }))}
            placeholder="Cliente, ambiente, vendedor..."
          />
          <FilterPanel
            compact
            title="Filtros da visão financeira"
            meta={`${filtrados.length} de ${ativos.length} ambientes ativos considerados`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Vendedor</FilterLabel>
                <SharedFiltroSelect
                  value={filters.vendedor}
                  onChange={(event) => setFilters((current) => ({ ...current, vendedor: event.target.value }))}
                >
                  {vendedorOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </SharedFiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Tipo de ambiente</FilterLabel>
                <SharedFiltroSelect
                  value={filters.tipo}
                  onChange={(event) => setFilters((current) => ({ ...current, tipo: event.target.value }))}
                >
                  {tipoOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </SharedFiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Situação</FilterLabel>
                <SharedFiltroSelect
                  value={filters.situacao}
                  onChange={(event) => setFilters((current) => ({ ...current, situacao: event.target.value }))}
                >
                  <option value="TODAS">Todas</option>
                  <option value="Entregue">Entregue</option>
                  <option value="Em atraso">Em atraso</option>
                  <option value="Em aberto">Em aberto</option>
                </SharedFiltroSelect>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          <BotaoExportacao
            titulo="Visão Financeira"
            resumo={exportResumo}
            secoes={exportSecoes}
            visual={exportVisual}
            filtrosAtivos={activeFilterChips}
          />
        </TopBarAcoes>
      </TopBar>

      <Abas>
        {[
          { id: "resumo", label: "Resumo" },
          { id: "vendedores", label: "Por Vendedor" },
          { id: "abc", label: "Curva ABC" },
        ].map((aba) => (
          <Aba key={aba.id} $active={abaAtiva === aba.id} onClick={() => setAbaAtiva(aba.id)}>
            {aba.label}
          </Aba>
        ))}
      </Abas>

      {abaAtiva === "resumo" && (
        <>
          <Secao data-pdf="kpis-receita">
            <SecaoTitulo>Receita</SecaoTitulo>
            <Grid>
              <KPICard label="Receita Total" valor={mask(formatarMoeda(kpis.receitaTotal))} delay={0.05} />
              <KPICard label="Faturado (Entregues)" valor={mask(formatarMoeda(kpis.receitaEntregues))} delay={0.1} />
              <KPICard label="WIP (Em Produção)" valor={mask(formatarMoeda(kpis.receitaWip))} delay={0.15} />
              <KPICard label="Ticket Médio / Ambiente" valor={mask(formatarMoeda(kpis.ticketMedioAmb))} delay={0.2} />
              <KPICard label="Ticket Médio / Cliente" valor={mask(formatarMoeda(kpis.ticketMedioCli))} delay={0.25} />
            </Grid>
          </Secao>

          <Secao data-pdf="kpis-producao">
            <SecaoTitulo>Produção e Prazos</SecaoTitulo>
            <Grid>
              <KPICard label="Total de Ambientes" valor={kpis.totalAmbientes} delay={0.3} />
              <KPICard label="Em Atraso" valor={kpis.atrasados} destaque={kpis.atrasados > 0} delay={0.35} />
              <KPICard label="% em Atraso" valor={`${kpis.percAtraso}%`} destaque={Number(kpis.percAtraso) > 20} delay={0.4} />
              <KPICard
                label="Dias Médios de Atraso"
                valor={kpis.diasMediosAtraso != null ? `${kpis.diasMediosAtraso} dias` : "—"}
                delay={0.45}
              />
            </Grid>
          </Secao>
        </>
      )}

      {abaAtiva === "vendedores" && (
        <Secao data-pdf="tabela-vendedores">
          <SecaoTitulo>Receita por Vendedor</SecaoTitulo>
          <ColumnResizeProvider
            tableId="vf-vendedores"
            persistirNoBackend
            columns={[
              { key: "vendedor", defaultWidth: null },
              { key: "qtd", defaultWidth: "120px" },
              { key: "receita", defaultWidth: "140px" },
              { key: "ticket", defaultWidth: "140px" },
              { key: "part", defaultWidth: "90px" },
            ]}
          >
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
            <ResetColumnsButton />
          </div>
          <TabelaWrapper>
            <Tabela>
              <ResizableColgroup />
              <thead>
                <tr>
                  <ResizableTh as={Th} columnKey="vendedor">Vendedor</ResizableTh>
                  <ResizableTh as={Th} columnKey="qtd">Qtd. Ambientes</ResizableTh>
                  <ResizableTh as={Th} columnKey="receita">Receita Total</ResizableTh>
                  <ResizableTh as={Th} columnKey="ticket">Ticket Médio</ResizableTh>
                  <ResizableTh as={Th} columnKey="part">Part. %</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {kpis.porVendedor.map((vendedor, index) => {
                  const participacao = kpis.receitaTotal
                    ? ((vendedor.receita / kpis.receitaTotal) * 100).toFixed(1)
                    : "0.0";

                  return (
                    <Tr key={vendedor.vendedor} $delay={index * 0.04}>
                      <Td>
                        <strong>{vendedor.vendedor}</strong>
                      </Td>
                      <Td>{vendedor.qtd}</Td>
                      <Td>{mask(formatarMoeda(vendedor.receita))}</Td>
                      <Td>{mask(formatarMoeda(vendedor.ticket))}</Td>
                      <TdBar>
                        <BarraInline $width={`${participacao}%`} />
                        <span>{participacao}%</span>
                      </TdBar>
                    </Tr>
                  );
                })}
              </tbody>
            </Tabela>
          </TabelaWrapper>
          </ColumnResizeProvider>
        </Secao>
      )}

      {abaAtiva === "abc" && (
        <Secao data-pdf="curva-abc">
          <SecaoTitulo>Curva ABC | Receita por Tipo de Ambiente</SecaoTitulo>
          <LegendaABC>
            <LegendaItem $cor={CLASSE_COR.A} $bg={CLASSE_BG.A}>Classe A | top 80% da receita</LegendaItem>
            <LegendaItem $cor={CLASSE_COR.B} $bg={CLASSE_BG.B}>Classe B | 80% a 95% da receita</LegendaItem>
            <LegendaItem $cor={CLASSE_COR.C} $bg={CLASSE_BG.C}>Classe C | 95% a 100% da receita</LegendaItem>
          </LegendaABC>

          <ColumnResizeProvider
            tableId="vf-curva-abc"
            persistirNoBackend
            columns={[
              { key: "classe", defaultWidth: "80px" },
              { key: "tipo", defaultWidth: null },
              { key: "qtd", defaultWidth: "80px" },
              { key: "entreg", defaultWidth: "90px" },
              { key: "aberto", defaultWidth: "90px" },
              { key: "receita", defaultWidth: "130px" },
              { key: "ticket", defaultWidth: "120px" },
              { key: "part", defaultWidth: "80px" },
              { key: "acum", defaultWidth: "90px" },
              { key: "visual", defaultWidth: "120px" },
            ]}
          >
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
            <ResetColumnsButton />
          </div>
          <TabelaWrapper>
            <Tabela>
              <ResizableColgroup />
              <thead>
                <tr>
                  <ResizableTh as={Th} columnKey="classe">Classe</ResizableTh>
                  <ResizableTh as={Th} columnKey="tipo">Tipo de Ambiente</ResizableTh>
                  <ResizableTh as={Th} columnKey="qtd">Qtd.</ResizableTh>
                  <ResizableTh as={Th} columnKey="entreg">Entregues</ResizableTh>
                  <ResizableTh as={Th} columnKey="aberto">Em Aberto</ResizableTh>
                  <ResizableTh as={Th} columnKey="receita">Receita Total</ResizableTh>
                  <ResizableTh as={Th} columnKey="ticket">Ticket Médio</ResizableTh>
                  <ResizableTh as={Th} columnKey="part">Part. %</ResizableTh>
                  <ResizableTh as={Th} columnKey="acum">Acumulado %</ResizableTh>
                  <ResizableTh as={Th} columnKey="visual">Participação Visual</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {kpis.curvaAbc.map((tipo, index) => (
                  <Tr key={tipo.tipo} $delay={index * 0.03}>
                    <Td>
                      <ClasseBadge $cor={CLASSE_COR[tipo.classe]} $bg={CLASSE_BG[tipo.classe]}>
                        {tipo.classe}
                      </ClasseBadge>
                    </Td>
                    <Td>
                      <TipoButton type="button" onClick={() => setTipoModalAberto(tipo.tipo)}>
                        {tipo.tipo}
                      </TipoButton>
                    </Td>
                    <Td>{tipo.qtd}</Td>
                    <Td>{tipo.entregues}</Td>
                    <Td>{tipo.qtd - tipo.entregues}</Td>
                    <Td>{mask(formatarMoeda(tipo.receita))}</Td>
                    <Td>{mask(formatarMoeda(tipo.ticket))}</Td>
                    <Td>{tipo.participacao}%</Td>
                    <Td>
                      <AcumBadge $classe={tipo.classe}>{tipo.acumulado}%</AcumBadge>
                    </Td>
                    <TdBar>
                      <BarraInline $width={`${tipo.participacao}%`} $background={CLASSE_COR[tipo.classe]} />
                      <span>{tipo.participacao}%</span>
                    </TdBar>
                  </Tr>
                ))}
              </tbody>
            </Tabela>
          </TabelaWrapper>
          </ColumnResizeProvider>

          <ResumoABC>
            {["A", "B", "C"].map((classe) => {
              const itens = kpis.curvaAbc.filter((tipo) => tipo.classe === classe);
              const receita = itens.reduce((accumulator, tipo) => accumulator + tipo.receita, 0);
              const qtd = itens.reduce((accumulator, tipo) => accumulator + tipo.qtd, 0);

              return (
                <ResumoCard key={classe} $cor={CLASSE_COR[classe]} $bg={CLASSE_BG[classe]}>
                  <ResumoClasse $cor={CLASSE_COR[classe]}>Classe {classe}</ResumoClasse>
                  <ResumoInfo>
                    {itens.length} tipo{itens.length !== 1 ? "s" : ""} de ambiente
                  </ResumoInfo>
                  <ResumoInfo>{qtd} ambiente{qtd !== 1 ? "s" : ""}</ResumoInfo>
                  <ResumoReceita>{mask(formatarMoeda(receita))}</ResumoReceita>
                </ResumoCard>
              );
            })}
          </ResumoABC>
        </Secao>
      )}

      <AnimatePresence>
        {tipoSelecionado && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setTipoModalAberto(null)}
          >
            <ModalCard
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <ModalTitle>{tipoSelecionado.tipo}</ModalTitle>
                  <ModalMeta>
                    {tipoSelecionado.qtd} ambiente{tipoSelecionado.qtd !== 1 ? "s" : ""} |{" "}
                    {tipoSelecionado.entregues} entregue{tipoSelecionado.entregues !== 1 ? "s" : ""} |{" "}
                    {tipoSelecionado.qtd - tipoSelecionado.entregues} em aberto
                  </ModalMeta>
                </div>
                <ModalCloseButton
                  type="button"
                  onClick={() => setTipoModalAberto(null)}
                  aria-label="Fechar detalhes"
                >
                  <FiX size={18} />
                </ModalCloseButton>
              </ModalHeader>

              <ModalTableWrapper>
                <ModalTable>
                  <thead>
                    <tr>
                      <Th>Cliente</Th>
                      <Th>Ambiente</Th>
                      <Th>Vendedor</Th>
                      <Th>Prazo</Th>
                      <Th>Situação</Th>
                      <Th>Valor</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {tipoSelecionado.ambientes.map((ambiente, index) => (
                      <tr key={`${ambiente.cliente}-${ambiente.ambiente}-${index}`}>
                        <Td>{ambiente.cliente}</Td>
                        <Td>{ambiente.ambiente}</Td>
                        <Td>{ambiente.vendedor}</Td>
                        <Td>{formatarData(ambiente.prazo)}</Td>
                        <Td>{ambiente.situacao}</Td>
                        <Td>{mask(formatarMoeda(ambiente.valor))}</Td>
                      </tr>
                    ))}
                  </tbody>
                </ModalTable>
              </ModalTableWrapper>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Wrapper>
  );
}
